import dayjs from "dayjs"
import { ThunkApiConfig } from "RootType"

import { DEFAULT_END_TIME, DEFAULT_START_TIME } from "../../constants"
import { IdAndName } from "../../types/sharedTypes"
import { isInternalTime } from "../../utils"
import { timeslots } from "../api/timeslots"
import { sliceInitialState } from "../reduxUtils"
import { SliceState } from "../types"
import { BookAsset, RecurringReservationProps } from "./types"
import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  Slice,
} from "@reduxjs/toolkit"

import { Asset } from "../assets/types"

export const bookAssetSetDefaultDateTime = createAsyncThunk<
  { start: string; end: string; date: string },
  undefined,
  ThunkApiConfig
>("bookAssets/bookAssetSetDefaultDateTime", async (_, { getState }) => {
  const state = getState()
  const timeslot = state.bookAsset

  if (
    !!timeslot?.start &&
    !!timeslot?.end &&
    isInternalTime(timeslot.start) &&
    isInternalTime(timeslot.end)
  ) {
    // Already have raw time strings
    return {
      start: timeslot.start,
      end: timeslot.end,
      date: timeslot.bookAsset.date || dayjs().toISOString(),
    }
  }

  // Try to use timeslot default values first (for consistency with matcher)
  const timeslotsData =
    timeslots.endpoints.fetchTimeslots.select(undefined)(state)
  if (timeslotsData?.data?.length) {
    const defaultTimeslot =
      timeslotsData.data.find((slot) => slot.is_default) ??
      timeslotsData.data[0]
    return {
      start: defaultTimeslot.from,
      end: defaultTimeslot.to,
      date: dayjs().toISOString(),
    }
  }

  // Fall back to constants if no timeslots available
  return {
    start: DEFAULT_START_TIME,
    end: DEFAULT_END_TIME,
    date: dayjs().toISOString(),
  }
})

export interface BookAssetState extends SliceState {
  bookAsset: BookAsset
}

const initialState: BookAssetState = {
  bookAsset: {},
  ...sliceInitialState,
}

const bookAssetSlice: Slice<BookAssetState> = createSlice({
  name: "bookAsset",
  initialState,
  reducers: {
    bookAssetAddUserId: (state, action: PayloadAction<string>) => {
      state.bookAsset.userEmail = action.payload
    },
    bookAssetAddBuilding: (state, action: PayloadAction<IdAndName>) => {
      state.bookAsset.building = action.payload
    },
    bookAssetAddTimezone: (state, action: PayloadAction<string>) => {
      state.bookAsset.timezone = action.payload
    },
    bookAssetAddAssetTypeId: (state, action: PayloadAction<string>) => {
      state.bookAsset.assetTypeId = action.payload
    },
    bookAssetAddAsset: (state, action: PayloadAction<Asset>) => {
      state.bookAsset.asset = action.payload
    },
    bookAssetAddDateTime: (
      state,
      action: PayloadAction<{ date: string; start: string; end: string }>,
    ) => {
      state.bookAsset.date = action.payload.date
      state.bookAsset.start = action.payload.start
      state.bookAsset.end = action.payload.end
    },
    bookAssetAddRecurring: (
      state,
      action: PayloadAction<RecurringReservationProps>,
    ) => {
      state.bookAsset.recurring = action.payload
    },

    clearBookAsset: (state, _action: PayloadAction) => {
      state.bookAsset = {}
    },
    setBookAsset: (state, action: PayloadAction<BookAsset>) => {
      state.bookAsset = action.payload
    },
  },

  extraReducers: (builder) => {
    builder.addCase(
      bookAssetSetDefaultDateTime.fulfilled,
      (state, { payload }) => {
        state.bookAsset.start = payload.start
        state.bookAsset.end = payload.end
        state.bookAsset.date = payload.date
      },
    )
    builder.addMatcher(
      timeslots.endpoints.fetchTimeslots.matchFulfilled,
      (state, { payload }) => {
        if (payload.length && !state.bookAsset.start && !state.bookAsset.end) {
          // Only auto-initialize if state is completely empty
          // Find default timeslot first, then fall back to first timeslot
          const defaultTimeslot =
            payload.find((slot) => slot.is_default) ?? payload[0]

          state.bookAsset.start = defaultTimeslot.from
          state.bookAsset.end = defaultTimeslot.to
        }
      },
    )
  },
})

export const bookAssetReducer = bookAssetSlice.reducer
export const {
  bookAssetAddUserId,
  bookAssetAddBuilding,
  bookAssetAddTimezone,
  bookAssetAddAssetTypeId,
  bookAssetAddAsset,
  bookAssetAddDateTime,
  bookAssetAddRecurring,
  clearBookAsset,
  setBookAsset,
} = bookAssetSlice.actions
